/**
 * Future Cartridge battle action menu — Phaser-wired DOM glass.
 *
 * Replaces the legacy 2×2 Phaser Rectangle + m5x7 BitmapText grid with
 * 4 translucent DOM buttons + a BACK button. Same surface cycles
 * between the three menu modes (main / moves / switch); BattleScene
 * drives the labels via setButtons() and the BACK affordance via
 * setBackVisible().
 *
 * Each button can carry an optional `type` (Pokémon type, e.g. 'fire')
 * which drives a 1.5 px focus border in the matching --type-X color.
 * Buttons without a type fall back to a --text-primary border on focus.
 *
 * Defensive iOS click-delay grace timer: taps within 350 ms of show()
 * are ignored — same root cause as the dialogue overlay's grace
 * (synthetic click event lands on a freshly-mounted overlay).
 *
 * Layout: anchored bottom-right via inline-style writes from this
 * module. 2 × 2 grid; BACK sits to the left of the grid (visible only
 * in moves / switch sub-menus). Sized for ≥44 px hit targets per iOS
 * HIG.
 */
import Phaser from 'phaser';
import { onTap } from './gestureRouter';
import { Z_DIALOGUE } from './zLayers';

const PARENT_ID = 'game';
const TAP_GRACE_MS = 350;
/** Round-3 playtest scale-up ("make all the combat ui pieces larger,
 *  we have room"): buttons grew 56→64 tall; width is now RESPONSIVE —
 *  up to 168 px per button, shrinking so the 2-column grid always
 *  fits with EDGE_MARGIN_PX breathing room (320 px canvases get
 *  ~146 px buttons instead of overflowing). */
const BTN_MAX_W = 168;
const BTN_H = 64;
const BTN_GAP = 12;
const BACK_W = 108;
/** BACK is a secondary affordance and does not need the primary
 *  buttons' 64 px — 44 is still an ample tap target (HIG minimum),
 *  and the 20 px it gives back is 20 px of stage. Its row is
 *  reserved even when hidden (see BACK_ROW_PX). */
const BACK_H = 44;
const EDGE_MARGIN_PX = 8;
/** Bottom inset accounts for Android gesture nav + iOS home indicator
 *  area. parent.clientHeight (#game = 100dvh) on Android Chrome can
 *  include the gesture nav region — items anchored < ~80 px from
 *  bottom render below the visible area. Clears the typical gesture
 *  nav (~48 px) with a thumb-reach buffer, and sits 10 px above the
 *  party-orb strip (16 px inset + 60 lead orb + 2 lift = top at 78). */
const BOTTOM_INSET = 88;

/** Vertical space held for the BACK row whether or not BACK is
 *  showing. The reservation is deliberate — the 2×2 grid must not
 *  shift under the player's thumb between the main menu and a
 *  sub-menu — so this is the ONLY dead band in the bottom stack, and
 *  shrinking BACK is the only way to shrink it without motion. */
const BACK_ROW_PX = BACK_H + BTN_GAP;

/** Top edge of the action stack (grid + reserved BACK row), px from
 *  canvas bottom. THE vertical-layout coupling point: battleLog
 *  anchors 12 px above this, the status panel above the log, and
 *  BattleScene.layout clears the log via battleLog's derived export.
 *  Previously four hand-synced magic numbers (292/304/391/403). */
export const ACTION_STACK_TOP_PX =
  BOTTOM_INSET + BACK_ROW_PX + (BTN_H + BTN_GAP) + BTN_H; // 284

export interface BattleActionLabel {
  text: string;
  /** Optional Pokémon type for the focus border tint (e.g. 'fire'). */
  type?: string;
  /** Disabled buttons render dimmed and ignore taps. */
  disabled?: boolean;
  /** PR 2.5 / EXP 4: persistent effectiveness badge for move buttons.
   *  One of 'good'|'neutral'|'bad'|'none' — drives a small filled
   *  circle in the corresponding --matchup-* token color at the
   *  trailing edge. Undefined → no badge (non-move-mode buttons). */
  matchup?: 'good' | 'neutral' | 'bad' | 'none';
}

export type BattleActionTapTarget = number | 'back';

export interface BattleActionsHandle {
  /** Show 4 buttons with the given labels. Hidden buttons (e.g. fewer
   *  than 4 moves available) pass null in the array. */
  setButtons(labels: Array<BattleActionLabel | null>): void;
  /** Show/hide the BACK button. */
  setBackVisible(visible: boolean): void;
  /** Hide all buttons (including BACK). */
  hide(): void;
  /** Subscribe to button taps. handler receives a 1-based slot (1-4)
   *  for primary buttons or 'back' for the BACK affordance. Returns a
   *  disposer. */
  onTap(handler: (target: BattleActionTapTarget) => void): () => void;
  /** Unmount + remove all listeners. Idempotent. */
  destroy(): void;
}

export function mountBattleActions(scene: Phaser.Scene): BattleActionsHandle {
  const parent = document.getElementById(PARENT_ID) ?? document.body;
  const root = document.createElement('div');
  root.className = 'battle-actions';
  root.style.position = 'absolute';
  // inset: 0 makes root fill #game so absolutely-positioned children
  // anchor against #game's edges. Without this, root is 0×0 and child
  // right/bottom values render off-screen at negative viewport coords.
  // (battleHpBar avoids this by appending its single el directly to
  // #game with no wrapping container.)
  root.style.inset = '0';
  root.style.pointerEvents = 'none';
  root.style.zIndex = String(Z_DIALOGUE - 1);
  root.style.display = 'none';
  parent.appendChild(root);

  // Four primary buttons (slots 1-4) + BACK.
  const buttons: HTMLDivElement[] = [];
  const backBtn = document.createElement('div');
  backBtn.className = 'battle-action battle-action--back';
  backBtn.textContent = '◀ BACK';
  backBtn.style.position = 'absolute';
  root.appendChild(backBtn);

  for (let i = 0; i < 4; i++) {
    const btn = document.createElement('div');
    btn.className = 'battle-action';
    btn.style.position = 'absolute';
    btn.dataset.slot = String(i + 1);
    root.appendChild(btn);
    buttons.push(btn);
  }

  let openedAt = 0;
  const tapListeners = new Set<(target: BattleActionTapTarget) => void>();
  const tapDisposers: Array<() => void> = [];

  function attachTaps(): void {
    detachTaps();
    for (let i = 0; i < buttons.length; i++) {
      const btn = buttons[i]!;
      const slot = i + 1;
      tapDisposers.push(
        onTap(btn, {}, (e) => {
          // iOS Safari synthetic click ~300 ms after touchend fires as
          // a MOUSE pointer event. Real user taps on a freshly-shown
          // menu fire as TOUCH pointer events. Grace only applies to
          // mouse pointers so a fast force-switch tap right after the
          // faint cinematic isn't swallowed. (Before this guard's
          // narrowing, the user reported "I hit Charmander but nothing
          // happened so we were stalled" — their touch tap was within
          // 350 ms of openedAt and got blanket-ignored.)
          if (e.pointerType !== 'touch' && performance.now() - openedAt < TAP_GRACE_MS) return;
          if (btn.dataset.disabled === 'true') return;
          for (const h of Array.from(tapListeners)) h(slot);
        }),
      );
    }
    tapDisposers.push(
      onTap(backBtn, {}, (e) => {
        if (e.pointerType !== 'touch' && performance.now() - openedAt < TAP_GRACE_MS) return;
        for (const h of Array.from(tapListeners)) h('back');
      }),
    );
  }
  function detachTaps(): void {
    for (const d of tapDisposers) d();
    tapDisposers.length = 0;
  }

  function position(): void {
    // Bottom-center anchoring (design doc rev 6 lines 305-310: action
    // buttons + dialogue panel share the bottom-center anchor zone as
    // mutually exclusive states). parent.clientWidth is safe — the
    // Android 100dvh quirk affects clientHeight, not width; BOTTOM_INSET
    // still clears the gesture-nav region.
    const w = parent.clientWidth;
    const btnW = Math.min(
      BTN_MAX_W,
      Math.floor((w - 2 * EDGE_MARGIN_PX - BTN_GAP) / 2),
    );
    const gridLeft = Math.round((w - (2 * btnW + BTN_GAP)) / 2);
    for (let i = 0; i < 4; i++) {
      const col = i % 2; // 0 = left col, 1 = right col
      const row = Math.floor(i / 2); // 0 = top row, 1 = bottom row
      const btn = buttons[i]!;
      btn.style.right = '';
      btn.style.top = '';
      btn.style.left = `${gridLeft + col * (btnW + BTN_GAP)}px`;
      // Grid sits directly above the reserved BACK row, so it never
      // shifts between main / sub-menus.
      btn.style.bottom = `${BOTTOM_INSET + BACK_ROW_PX + (1 - row) * (BTN_H + BTN_GAP)}px`;
      btn.style.width = `${btnW}px`;
      btn.style.height = `${BTN_H}px`;
    }
    // BACK centered below the grid (visible in moves/switch sub-menus,
    // hidden in main). Centered horizontally rather than left-of-grid
    // because a 96 px BACK left-of-grid would go off-screen on phones
    // ≤ ~440 px wide.
    backBtn.style.right = '';
    backBtn.style.top = '';
    backBtn.style.left = `${Math.round((w - BACK_W) / 2)}px`;
    backBtn.style.bottom = `${BOTTOM_INSET}px`;
    backBtn.style.width = `${BACK_W}px`;
    backBtn.style.height = `${BACK_H}px`;
  }
  position();

  let resizeQueued = false;
  function onResize(): void {
    if (resizeQueued) return;
    resizeQueued = true;
    requestAnimationFrame(() => {
      resizeQueued = false;
      position();
    });
  }
  window.visualViewport?.addEventListener('resize', onResize);
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);

  const onSleep = (): void => {
    root.style.display = 'none';
  };
  const onWake = (): void => {
    // Don't auto-show on wake — BattleScene re-issues setButtons when
    // the next turn gate releases. Just keep DOM in the tree.
  };
  scene.events.on(Phaser.Scenes.Events.SLEEP, onSleep);
  scene.events.on(Phaser.Scenes.Events.WAKE, onWake);

  return {
    setButtons(labels): void {
      for (let i = 0; i < 4; i++) {
        const btn = buttons[i]!;
        const label = labels[i] ?? null;
        if (!label) {
          btn.style.display = 'none';
          btn.textContent = '';
          delete btn.dataset.type;
          delete btn.dataset.disabled;
          continue;
        }
        btn.style.display = 'flex';
        btn.textContent = label.text;
        if (label.type) btn.dataset.type = label.type.toLowerCase();
        else delete btn.dataset.type;
        btn.dataset.disabled = label.disabled ? 'true' : 'false';
        if (label.matchup) btn.dataset.matchup = label.matchup;
        else delete btn.dataset.matchup;
      }
      root.style.display = 'block';
      // Refresh tap listeners so any stale pointer-event state from a
      // previous show() doesn't leak. Reset openedAt so the iOS click-
      // delay grace timer covers the new gesture window.
      openedAt = performance.now();
      attachTaps();
    },
    setBackVisible(visible): void {
      backBtn.style.display = visible ? 'flex' : 'none';
    },
    hide(): void {
      root.style.display = 'none';
      detachTaps();
    },
    onTap(handler): () => void {
      tapListeners.add(handler);
      return () => tapListeners.delete(handler);
    },
    destroy(): void {
      detachTaps();
      scene.events.off(Phaser.Scenes.Events.SLEEP, onSleep);
      scene.events.off(Phaser.Scenes.Events.WAKE, onWake);
      window.visualViewport?.removeEventListener('resize', onResize);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      tapListeners.clear();
      root.remove();
    },
  };
}
